import React, {useEffect} from 'react';
import {getLinkPath} from 'app/utils';
import {inject} from 'mobx-react';
import {observer} from 'mobx-react-lite';
import SmartLoader from 'app/components/reusable/SmartLoader';
import {UserBonusesStore} from 'app/store/UserBonusesStore';
import {UserStore} from 'app/store/UserStore';
import I18n from 'app/i18n';
import ShopItems from 'app/components/shop/lootboxes/shop_items';
import BonusCard from 'app/components/my_profile/bonuses/BonusesCard';
import ActiveBonusesCard from 'app/components/my_profile/bonuses/ActiveBonusesCard';
import DepositBonusesCard from 'app/components/my_profile/bonuses/DepositBonusesCard';
import PromoBonusesCard from 'app/components/my_profile/bonuses/PromoBonusesCard';
import SpriteIcon from 'app/utils/sprites/SpriteIcon';
import ClickableTooltip from '../reusable/ClickableTooltip';
import {Link} from 'react-router-dom';
import {Swiper, SwiperSlide} from 'swiper/swiper-react';
import {Pagination, Navigation} from 'swiper/modules';
import {ArticlesStore} from 'app/store/ArticlesStore';
import {pushAnalyticsEvent} from 'app/utils/analytics';

interface MyBonusesPageInterface {
    userBonusesStore?: UserBonusesStore
    userStore?: UserStore
    articlesStore?: ArticlesStore
}

const swiperParams = {
    allowTouchMove: true,
    freeMode: {
        momentum: false,
        momentumBounce: false
    },
    observeParents: true,
    observer: true,
    slidesPerGroup: 2,
    speed: 400,
    touchRatio: 0.65,
    touchReleaseOnEdges: false,
    touchStartForcePreventDefault: true
};

const MOBILE_WIDTH = 580;
const MIN_BONUSES_FOR_PAGINATION = 3;

const getTooltipContent = () => <div className="bonuses-tooltip-content">
    <p className="bonuses-tooltip-content__title">{I18n.t('bonuses_page.tooltip_content_title')}</p>
    <ul className="bonuses-tooltip-content__list">
        <li>{I18n.t('bonuses_page.tooltip_content_text_1')}</li>
        <li>{I18n.t('bonuses_page.tooltip_content_text_2')}</li>
    </ul>
    <Link to={getLinkPath('/faq/bonus')} className="bonuses-tooltip-content__link">{I18n.t('read_more_u')}</Link>
</div>;

const MyBonusesPage: React.FC<MyBonusesPageInterface> = props => {
    const isMobile = window.innerWidth <= MOBILE_WIDTH;
    const {userBonusesStore, userStore, articlesStore} = props;
    const {promoNewData: {user_promo_view}, promoActionsBonuses, createUserNewsView, hideExpiredPromoNewData} = articlesStore;
    const {availableBonuses, activeBonus, bonusesLoading, bonusDataLoading, bonusesLootboxes,
        activateBonus, cancelBonus, markBonusViewed, removeBonusAfterCancel} = userBonusesStore;
    const {bonus_balance, first_deposit, first_bonus_not_applied} = userStore.user;
    const hasActiveBonus = Boolean(activeBonus);
    const showDepositBonusCard = !first_deposit || first_bonus_not_applied;
    const hasBonuses = availableBonuses.length > 0 || promoActionsBonuses.length > 0 || showDepositBonusCard;
    const hidePaginationControl = availableBonuses.length + promoActionsBonuses.length + Number(showDepositBonusCard) < MIN_BONUSES_FOR_PAGINATION;

    useEffect(() => {
        if (!bonusesLootboxes?.length) {
            userBonusesStore.initBonusesLootboxes();
        }
    }, [bonusesLootboxes]);

    useEffect(() => {
        if (!availableBonuses?.length) {
            return;
        }

        availableBonuses
            .filter(bonus => !bonus.viewed)
            .forEach(bonus => {
                pushAnalyticsEvent('bonus_view', {
                    bonus_id: bonus.id,
                    bonus_type: bonus.type,
                    source: 'my_bonuses'
                });
                markBonusViewed(bonus.id);
            });
    }, [availableBonuses, markBonusViewed]);

    useEffect(() => {
        if (!user_promo_view?.length || !promoActionsBonuses?.length) {
            return;
        }

        const viewedMap = new Map(user_promo_view.map(item => [item.id, item.viewed]));
        promoActionsBonuses
            .filter(promo => {
                const viewed = viewedMap.get(promo.id);
                return viewed === false;
            })
            .forEach(promo => {
                pushAnalyticsEvent('bonus_view', {
                    bonus_id: promo.id,
                    bonus_type: promo.bonus_type,
                    source: 'promo_bonus'
                });
                createUserNewsView(promo);
            });
    }, [
        user_promo_view,
        promoActionsBonuses,
        createUserNewsView
    ]);

    return <>
        {bonusDataLoading || bonusesLoading
            ? <SmartLoader/>
            : <div id="bonuses" className="bonuses profile-tab">
                <div className="bonuses__container">
                    <div className="bonuses__container-active">
                        <h3 className="bonuses__container-title">{I18n.t('bonuses_page.active_bonus')}</h3>
                        <ActiveBonusesCard
                            activeBonus={activeBonus}
                            cancelBonus={cancelBonus}
                            bonus_balance={bonus_balance}
                            hideExpiredBonus={removeBonusAfterCancel}
                        />
                        <p className="bonuses__container-text">
                            {I18n.t('bonuses_page.only_one_active_bonus')}
                        </p>
                    </div>
                    <div className="bonuses__container-available">
                        <h3 className="bonuses__container-title">
                            <span>{I18n.t('bonuses_page.available_bonuses')}</span>
                            <div className="bonuses-tooltip">
                                <ClickableTooltip text={getTooltipContent()} svg="#svg-question" width={16} height={16}/>
                            </div>
                        </h3>
                        {hasBonuses && <div className="bonuses__container-cards">
                            {isMobile
                                ? <>
                                    <Swiper
                                        className="slider-container-swiper"
                                        {...swiperParams}
                                        slidesPerView={'auto'}
                                        modules={[Pagination, Navigation]}
                                        pagination={{
                                            clickable: true,
                                            el: '.pagination-my-bonuses'
                                        }}
                                        navigation={{
                                            nextEl: '.button-my-bonuses-next',
                                            prevEl: '.button-my-bonuses-prev'
                                        }}
                                        allowTouchMove
                                        preventClicksPropagation
                                        preventInteractionOnTransition
                                        speed={300}
                                    >
                                        {showDepositBonusCard && <SwiperSlide>
                                            <DepositBonusesCard />
                                        </SwiperSlide>}
                                        {availableBonuses.map(bonus => <SwiperSlide key={`my-bonuses-slide-${bonus.id}`}>
                                            <BonusCard
                                                activateBonus={activateBonus}
                                                hasActiveBonus={hasActiveBonus}
                                                hideExpiredBonus={removeBonusAfterCancel}
                                                {...bonus}
                                            />
                                        </SwiperSlide>)}
                                        {promoActionsBonuses.length > 0 && promoActionsBonuses.map(promo => <SwiperSlide
                                            key={`my-bonuses-slide-promo-${promo.id}`}
                                        >
                                            <PromoBonusesCard
                                                hideExpiredPromoNewData={hideExpiredPromoNewData}
                                                {...promo}
                                            />
                                        </SwiperSlide>)}
                                    </Swiper>
                                    <div className={`slider-pagination-control ${hidePaginationControl ? 'hidden' : ''}`}>
                                        <button className={'swiper-button-slider-prev button-my-bonuses-prev'}>
                                            <SpriteIcon name="#svg-swiper-arrow" className="svg-default"/>
                                        </button>
                                        <div className={'swiper-pagination-group pagination-my-bonuses'}/>
                                        <button className={'swiper-button-slider-next button-my-bonuses-next'}>
                                            <SpriteIcon name="#svg-swiper-arrow" className="svg-default"/>
                                        </button>
                                    </div>
                                </>
                                : <>
                                    {showDepositBonusCard && <DepositBonusesCard />}
                                    {availableBonuses.map(bonus => <BonusCard
                                        key={bonus.id}
                                        activateBonus={activateBonus}
                                        hasActiveBonus={hasActiveBonus}
                                        hideExpiredBonus={removeBonusAfterCancel}
                                        {...bonus}
                                    />)}
                                    {promoActionsBonuses.length > 0 && promoActionsBonuses.map(promo => <PromoBonusesCard
                                        key={`my-bonuses-slide-promo-${promo.id}`}
                                        hideExpiredPromoNewData={hideExpiredPromoNewData}
                                        {...promo}
                                    />)}
                                </>
                            }
                        </div>}
                        {!hasBonuses && <div className="bonuses__list-empty">
                            <div className="smile">:(</div>
                            <div className="bonuses__list-empty__text">
                                <div className="subtitle">
                                    {I18n.t('no_bonuses')}
                                </div>
                            </div>
                        </div>}
                    </div>
                </div>
                <div className="market__lootboxes">
                    <ShopItems lootboxes={bonusesLootboxes} user={userStore.user} noChunks hiddenEmpty/>
                </div>
            </div>}
    </>;
};
export default inject('userBonusesStore', 'userStore', 'articlesStore')(observer(MyBonusesPage));
